import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { RemoteDiagnosticsHelpersService, SmartNotesWebsocketResponse } from '@vi-iot/client';

import { ActiveErrorData, DisplayedErrorState } from '@store';

import { ActiveErrorsActions } from './active-errors.actions';
import { ActiveErrorsSelectors } from './active-errors.selectors';

@Injectable({ providedIn: 'root' })
export class ActiveErrorsStore {
  isLoading$ = this.store.select(ActiveErrorsSelectors.isLoading);
  loaded$ = this.store.select(ActiveErrorsSelectors.loaded);
  selectedActiveError$ = this.store.select(ActiveErrorsSelectors.getSelectedActiveError);
  isAnyActiveErrorLoading$ = this.store.select(ActiveErrorsSelectors.isAnyActiveErrorLoading);
  hasAnyActiveErrorDetails$ = this.store.select(ActiveErrorsSelectors.hasAnyActiveErrorDetails);
  activeErrorsTrending$ = this.store.select(
    ActiveErrorsSelectors.getActiveGatewayErrorsForTrending()
  );
  selectDeviceIdForLowBatteryNotification$ = this.store.select(
    ActiveErrorsSelectors.selectDeviceIdForLowBatteryNotification
  );

  isDeviceOrLockedSystemError$: Observable<boolean> = this.store.select(
    ActiveErrorsSelectors.isDeviceOrLockedSystemError()
  );

  constructor(
    private store: Store,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService
  ) {}

  loadActiveErrors(installationId: number): void {
    this.store.dispatch(
      ActiveErrorsActions.readActiveErrors.attempt({
        installationId,
      })
    );
  }

  liveUpdateActiveError(smartNote: SmartNotesWebsocketResponse): void {
    this.store.dispatch(ActiveErrorsActions.liveUpdateActiveError.attempt({ smartNote }));
  }

  selectActiveError(gatewaySerial: string, activeErrorId: number, isLockedSystem?: boolean): void {
    this.remoteDiagnosticsHelpersService.cancelRequests$.next();
    this.store.dispatch(
      ActiveErrorsActions.selectActiveError({ gatewaySerial, activeErrorId, isLockedSystem })
    );
  }

  floorHeatingOverHeatingError(gatewaySerial: string): Observable<ActiveErrorData> {
    return this.store.select(ActiveErrorsSelectors.getFloorHeatingOverHeatingError(gatewaySerial));
  }

  lockedSystemError(gatewaySerial: string): Observable<ActiveErrorData> {
    return this.store.select(ActiveErrorsSelectors.getLockedSystemError(gatewaySerial));
  }

  isLockedSystemError(gatewaySerial: string): Observable<boolean> {
    return this.store.select(ActiveErrorsSelectors.isLockedSystemError(gatewaySerial));
  }

  getActiveGatewayErrors(
    gatewaySerial: string,
    deviceSerial: string = undefined,
    includeDeviceTypes = false,
    withDetailsOnly = false
  ): Observable<ActiveErrorData[]> {
    return this.store.select(
      ActiveErrorsSelectors.getActiveGatewayErrors(
        gatewaySerial,
        deviceSerial,
        includeDeviceTypes,
        withDetailsOnly
      )
    );
  }

  getActiveErrorsForSRCDevice(deviceId: string): Observable<ActiveErrorData[]> {
    return this.store.select(ActiveErrorsSelectors.getActiveErrorsForSRCDevice(deviceId));
  }

  isAnyErrorSelectedForDeviceBySerial(deviceSerial: string): Observable<boolean> {
    return this.store.select(
      ActiveErrorsSelectors.isAnyErrorSelectedForDeviceBySerial(deviceSerial)
    );
  }

  selectFirstActiveErrorForThisDevice(gatewaySerial: string, deviceSerial: string): void {
    return this.store.dispatch(
      ActiveErrorsActions.selectFirstActiveErrorForDevice({ gatewaySerial, deviceSerial })
    );
  }

  isLowBatteryStatusOfSRCDevice(deviceId: string): Observable<boolean> {
    return this.store.select(ActiveErrorsSelectors.isLowBatteryStatusOfSRCDevice(deviceId));
  }

  changeErrorsVisibility(): void {
    this.store.dispatch(ActiveErrorsActions.changeVisibilityOfErrorsCards());
  }

  getDisplayedActiveErrorsAndNotificationsModel(
    gatewaySerial: string,
    includeDeviceTypes: boolean,
    withDetailsOnly: boolean
  ): Observable<DisplayedErrorState> {
    return this.store.select(
      ActiveErrorsSelectors.getDisplayedActiveErrorsAndNotificationsModel(
        gatewaySerial,
        includeDeviceTypes,
        withDetailsOnly
      )
    );
  }

  getMaintenanceNotificationsDistinctByType(gatewaySerial: string): Observable<ActiveErrorData[]> {
    return this.store.select(
      ActiveErrorsSelectors.getMaintenanceNotificationsDistinctByType(gatewaySerial)
    );
  }
}
